/**
 * Supported search filter keys
 */
export const FILTER_KEYS = ['name', 'type', 'kind', 'owner', 'lifecycle'] as const;

export type FilterKey = typeof FILTER_KEYS[number];

/**
 * Parsed search query result
 */
export interface ParsedQuery {
  filters: {
    name?: string;
    type?: string;
    kind?: string;
    owner?: string;
    lifecycle?: string;
  };
  term: string;
}

/**
 * Parse GitHub-like search syntax from a query string.
 * 
 * Supports the following filter syntax:
 * - name:value - Filter by documentation name (techdocs)
 * - type:value - Filter by search type (software-catalog, techdocs)
 * - kind:value - Filter by entity kind (Component, Template, etc.)
 * - owner:value - Filter by entity owner
 * - lifecycle:value - Filter by entity lifecycle (experimental, production)
 * 
 * Examples:
 *   parseSearchQuery("name:navis backchannel")
 *   // Returns: { filters: { name: "navis" }, term: "backchannel" }
 * 
 *   parseSearchQuery("owner:team-a kind:Component api")
 *   // Returns: { filters: { owner: "team-a", kind: "Component" }, term: "api" }
 * 
 *   parseSearchQuery('name:"my docs" search term')
 *   // Returns: { filters: { name: "my docs" }, term: "search term" }
 * 
 * @param query - The search query string to parse
 * @returns Parsed filters and remaining search term
 */
export function parseSearchQuery(query: string): ParsedQuery {
  const filters: ParsedQuery['filters'] = {};
  let remainingText = query;

  // Regular expression to match filter patterns:
  // - filterKey: matches any of the supported filter keys
  // - value: matches either a quoted string or a non-whitespace word
  const filterPattern = new RegExp(
    `(${FILTER_KEYS.join('|')}):((?:"[^"]*")|(?:[^\\s]+))`,
    'gi'
  );

  // Extract all filter matches
  let match: RegExpExecArray | null;
  while ((match = filterPattern.exec(query)) !== null) {
    const key = match[1].toLowerCase() as FilterKey;
    let value = match[2];

    // Remove surrounding quotes if present
    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1);
    }

    // Store the filter (last occurrence wins if duplicates)
    filters[key] = value;
  }

  // Remove all filter patterns from the query to get the remaining search term
  remainingText = query.replace(filterPattern, '').trim();

  // Normalize multiple spaces to single space
  remainingText = remainingText.replace(/\s+/g, ' ');

  return {
    filters,
    term: remainingText,
  };
}
